import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  Action,
  ActionType,
  SuspiciousCardTxnActionType,
  SuspiciousCardTxnDetailsTile,
  SuspiciousCardTxnQuestionActionTile,
  SuspiciousCardTxnQuestionTile,
  TileType,
  Typenames,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  Stack,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import SuspiciousTransactionDeclinedBottomSheet from 'features/suspiciousTransaction/components/SuspiciousTransactionDeclinedBottomSheet/SuspiciousTransactionDeclinedBottomSheet';
import useReplaceCard from 'features/suspiciousTransaction/hooks/useReplaceCard';
import { useSubmitSuspiciousTransactionStatus } from 'features/suspiciousTransaction/hooks/useSubmitSuspiciousTransactionStatus';
import useSuspiciousCardTransactionDetails from 'features/suspiciousTransaction/hooks/useSuspiciousCardTransactionDetails';
import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Button from 'shared/components/vxl/Button/Button';
import Icon from 'shared/components/vxl/icon/Icon/Icon';
import IconButton from 'shared/components/vxl/icon/IconButton/IconButton';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import RadioButtonGroup from 'shared/components/vxl/selection/RadioButton/RadioButtonGroup';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmBoldText from 'shared/components/vxl/typography/primary/body/BodySmBoldText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import BodyXSText from 'shared/components/vxl/typography/primary/body/BodyXSText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import { isIOS } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import { useRefreshSystemBanners } from 'shared/hooks/homeTiles/useRefreshHomeTiles';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';

interface DetailRendererProps {
  label: string;
  value: string;
}

const DetailRenderer = memo(({ label, value }: DetailRendererProps) => {
  return (
    <View style={styles.row}>
      <View style={styles.labelDetailWrapper}>
        <BodySmText numberOfLines={1}>{label}</BodySmText>
      </View>
      <View style={styles.valueDetailWrapper}>
        <BodySmBoldText numberOfLines={1}>{value}</BodySmBoldText>
      </View>
    </View>
  );
});

const SuspiciousTransaction = () => {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { t: translatedText } = useTranslation('suspiciousTransaction', {
    keyPrefix: 'suspiciousTransactionDetails',
  });
  const [txnQuestionTileSelectedOption, setTxnQuestionTileSelectedOption] =
    useState('');
  const [refreshing, setRefreshing] = useState(false);
  const {
    onSubmitSuspiciousCardTransactionStatus,
    fetching: updatingTransactionStatus,
  } = useSubmitSuspiciousTransactionStatus();
  const { handleReplaceCard, fetching: replaceCardFetching } = useReplaceCard();
  const { defaultTitle } = useDefaultErrorStrings();
  const SuspiciousTransactionDeclinedBottomSheetRef =
    useRef<BottomSheetModal>(null);
  const { id } = useLocalSearchParams<{ id: string }>();
  const { suspiciousCardTxnTile, fetching, error, refetchQuery } =
    useSuspiciousCardTransactionDetails({
      transactionId: id,
    });
  const { segmentClient } = useAnalyticsConfiguration();
  const { refreshSystemBanners } = useRefreshSystemBanners();

  const backToPreviousScreen = () => {
    router.back();
  };

  useViewDidFocusScreenAnalyticsEvent({
    key: '/suspicious-transaction',
  });

  useEffect(() => {
    if (segmentClient && txnQuestionTileSelectedOption !== '') {
      void segmentClient.logEvent('Customer Response Selected', {
        customer_response: txnQuestionTileSelectedOption,
        source_name: 'suspicious_transaction_review_page',
      });
    }
  }, [txnQuestionTileSelectedOption, segmentClient]);

  const submitSuspiciousTransaction = async (
    type: SuspiciousCardTxnActionType,
  ) => {
    const response = await onSubmitSuspiciousCardTransactionStatus({
      type,
      transactionId: id,
    });
    switch (response?.data?.suspiciousCardTxnAction?.__typename) {
      case Typenames.SuspiciousCardTxnActionApproveSuccess:
        router.replace({
          pathname: '/suspicious-transaction/transaction-result',
          params: {
            resultType: response.data.suspiciousCardTxnAction.__typename,
            title: response.data.suspiciousCardTxnAction.successTitle,
            message: response.data.suspiciousCardTxnAction.successMessage,
          },
        });
        break;
      case Typenames.SuspiciousCardTxnActionDeclineSuccess:
        if (response.data.suspiciousCardTxnAction.isReplaceable) {
          const responseOfHandleReplaceCard = await handleReplaceCard({
            cardId: response.data.suspiciousCardTxnAction.cardId,
          });
          if (
            responseOfHandleReplaceCard?.data?.replaceLostStolenCard?.__typename
          ) {
            router.replace({
              pathname: '/suspicious-transaction/transaction-result',
              params: {
                resultType: response.data.suspiciousCardTxnAction.__typename,
                title:
                  responseOfHandleReplaceCard.data.replaceLostStolenCard.title,
                message:
                  responseOfHandleReplaceCard.data.replaceLostStolenCard
                    .message,
              },
            });
          }
        } else {
          router.push({
            pathname: '/myvaro/help/chat',
          });
        }
        break;
      case Typenames.SuspiciousCardTxnActionError:
        router.replace({
          pathname: '/suspicious-transaction/transaction-result',
          params: {
            resultType: response.data.suspiciousCardTxnAction.__typename,
            title: response.data.suspiciousCardTxnAction.title,
            message: response.data.suspiciousCardTxnAction.message,
          },
        });
        break;
      default:
        break;
    }
  };

  const onPressSuspiciousTransactionCta = () => {
    if (txnQuestionTileSelectedOption === 'yes') {
      void submitSuspiciousTransaction(SuspiciousCardTxnActionType.Approve);
      return;
    } else if (txnQuestionTileSelectedOption === 'no') {
      SuspiciousTransactionDeclinedBottomSheetRef?.current?.present();
      return;
    }
    refreshSystemBanners();
  };

  const onPressBottomSheetCtaAction = async (cta: Action) => {
    if (cta.action === ActionType.DontCancelSuspiciousTransaction) {
      SuspiciousTransactionDeclinedBottomSheetRef?.current?.close();
      return;
    } else if (cta.action === ActionType.CancelSuspiciousTransaction) {
      await submitSuspiciousTransaction(SuspiciousCardTxnActionType.Decline);
      SuspiciousTransactionDeclinedBottomSheetRef?.current?.close();
      return;
    }
  };

  const onRefresh = () => {
    setRefreshing(true);
    refetchQuery();
    setRefreshing(false);
  };

  return (
    <View
      style={[
        styles.container,
        {
          paddingBottom: isIOS
            ? insets.bottom
            : insets.bottom + theme.spacing.m,
        },
      ]}
    >
      <Stack.Screen
        options={{
          headerShown: true,
          // navigation bar doesn't center the X if no title is added.
          title: ' ',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar
              primaryIcon={
                <IconButton
                  iconKey="CLOSE"
                  iconOnPress={backToPreviousScreen}
                  iconButtonAccessibilityLabel="Close"
                />
              }
              {...props}
            />
          ),
        }}
      />

      <ScrollView
        contentContainerStyle={styles.gap}
        showsVerticalScrollIndicator={false}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        <View style={styles.centerAligned}>
          <Icon
            iconAccessibilityLabel="error-icon"
            iconKey="ERROR"
            iconSize={100}
          />
        </View>
        <TitleLgText numberOfLines={0}>
          {suspiciousCardTxnTile?.title}
        </TitleLgText>
        {suspiciousCardTxnTile?.message ? (
          <BodyBaseText numberOfLines={3}>
            {suspiciousCardTxnTile.message}
          </BodyBaseText>
        ) : null}
        {suspiciousCardTxnTile?.tiles?.map((tile) => {
          switch (tile.tileType) {
            case TileType.SuspiciousCardTxnDetailsTile: {
              const txnDetailsTile = tile as SuspiciousCardTxnDetailsTile;
              return (
                <View key={txnDetailsTile.tileType} style={styles.detailTile}>
                  <DetailRenderer
                    label={translatedText('date')}
                    value={txnDetailsTile.txnDate}
                  />
                  <DetailRenderer
                    label={translatedText('merchant')}
                    value={txnDetailsTile.merchant}
                  />
                  <DetailRenderer
                    label={translatedText('amount')}
                    value={txnDetailsTile.txnAmount.formatted}
                  />
                  <DetailRenderer
                    label={translatedText('card')}
                    value={`${txnDetailsTile.cardLast4}`}
                  />
                </View>
              );
            }
            case TileType.SuspiciousCardTxnQuestionTile: {
              const txnQuestionTile = tile as SuspiciousCardTxnQuestionTile;
              return (
                <View key={tile.tileType}>
                  <BodyBaseBoldText numberOfLines={2}>
                    {txnQuestionTile?.question?.questionText}
                  </BodyBaseBoldText>
                  <RadioButtonGroup
                    options={
                      txnQuestionTile?.question?.options?.map((option) => {
                        return {
                          ...option,
                          labelText: option.label,
                        };
                      }) ?? []
                    }
                    onPress={(value) => setTxnQuestionTileSelectedOption(value)}
                  />
                </View>
              );
            }
            default:
              return null;
          }
        })}
      </ScrollView>

      <BodyXSText numberOfLines={0} style={styles.noteText}>
        {translatedText('noteLabel')}
      </BodyXSText>

      <Button
        label={suspiciousCardTxnTile?.ctaAction?.copy || ''}
        isDisabled={!txnQuestionTileSelectedOption}
        isLoading={
          updatingTransactionStatus && txnQuestionTileSelectedOption === 'yes'
        }
        onPress={onPressSuspiciousTransactionCta}
        analyticsId="customer_response_submitted"
        customAttributes={{ customer_response: txnQuestionTileSelectedOption }}
      />
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={error?.message ?? ''}
              tryAgainAction={refetchQuery}
            />
          ) : null
        }
      />
      <SuspiciousTransactionDeclinedBottomSheet
        data={
          suspiciousCardTxnTile?.tiles?.find(
            (tile) =>
              tile.tileType === TileType.SuspiciousCardTxnQuestionActionTile,
          ) as SuspiciousCardTxnQuestionActionTile
        }
        suspiciousTransactionDeclinedBottomSheetRef={
          SuspiciousTransactionDeclinedBottomSheetRef
        }
        onPressCtaAction={onPressBottomSheetCtaAction}
        updatingTransactionStatus={updatingTransactionStatus}
        replaceCardFetching={replaceCardFetching}
      />
    </View>
  );
};

const styles = StyleSheet.create({
  centerAligned: {
    alignItems: 'center',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
  },
  detailTile: {
    backgroundColor: theme.color.background.surface,
    borderRadius: theme.spacing.xs,
    gap: theme.spacing.s,
    padding: theme.spacing.m,
    width: '100%',
  },
  gap: {
    gap: theme.spacing.m,
  },
  labelDetailWrapper: {
    flex: 1.2,
  },
  noteText: {
    color: theme.color.text.secondary,
    paddingVertical: theme.spacing.m,
    textAlign: 'left',
  },
  row: {
    flexDirection: 'row',
  },
  valueDetailWrapper: {
    flex: 4.8,
  },
});

export default SuspiciousTransaction;
